import z from 'zod'
import { type TaskOriginId, TaskOriginIdSchema } from '../task'
import { QuestionIdSchema } from '../question'
import { ProgramTaskIdSchema } from '../program/programTask'
import { isValidLatLngBoundingBox, LAT_LNG_BOUNDING_BOX_ERROR_MESSAGE, LatLngSchema } from '../locations/latLng'
import {
  AccountStatusIdSchema,
  type ProgramId,
  ProgramIdSchema,
  TeamIdSchema,
  WorkflowIdSchema,
  WorkflowStageIdSchema,
} from '../ids'
import { GameIdSchema } from '../game/shared'
import { TinyTeamSchema, TinyUserSchema } from '../entity'
import { type DispositionId, DispositionIdSchema } from '../disposition'
import { CustomFieldIdSchema } from '../customField'
import { type EventType } from '../calendar'
import { ReportKpiFilterSchema } from './kpi'

export const ReportMapFilterSchema = z.object({
  ...ReportKpiFilterSchema.shape,
  contactDispositions: DispositionIdSchema.array().optional(),
  accountStatusIds: AccountStatusIdSchema.array().optional().meta({ deprecated: true }),
  workflowId: WorkflowIdSchema.optional(),
  workflowStageId: WorkflowStageIdSchema.optional(),
})
export type ReportMapFilter = z.infer<typeof ReportMapFilterSchema>

export const GeoSearchWindowSchema = z
  .object({
    topLeft: LatLngSchema,
    bottomRight: LatLngSchema,
  })
  .refine(({ topLeft, bottomRight }) => isValidLatLngBoundingBox({ top: topLeft, bottom: bottomRight }), {
    message: LAT_LNG_BOUNDING_BOX_ERROR_MESSAGE,
  })
export type GeoSearchWindow = z.infer<typeof GeoSearchWindowSchema>

export const ACCOUNT_ROLES = ['setter', 'closer'] as const
export type AccountRole = (typeof ACCOUNT_ROLES)[number]
export const AccountRoleSchema = z.enum(ACCOUNT_ROLES)
export const isAccountRole = (val: string): val is AccountRole => ACCOUNT_ROLES.some((role) => role === val)

export const AccountStatsByStatusKeySchema = z.enum([
  'count',
  'lastDate',
  'avgPerDay',
  'avgPerWeek',
  'teamAvgPerDay',
  'teamAvgPerWeek',
  'avgStartOfDay',
  'avgEndOfDay',
])
export type AccountStatsByStatusKey = z.infer<typeof AccountStatsByStatusKeySchema>
const AccountStatsByStatusSchema = z.object({
  count: z.number().optional(),
  lastDate: z.number().optional(),
  avgPerDay: z.number().optional(),
  avgPerWeek: z.number().optional(),
})
export type AccountStatsByStatus = z.infer<typeof AccountStatsByStatusSchema>

const AccountStatsByRoleKeysSchema = z.union([
  AccountStatusIdSchema,
  WorkflowStageIdSchema,
  z.literal('lead'),
  z.literal('sat'),
  z.literal('set'),
  z.literal('closed'),
  z.literal('approved'),
  z.literal('installed'),
  z.literal('archived'),
])
export type AccountStatsByRoleKeys = z.infer<typeof AccountStatsByRoleKeysSchema>

const AccountStatsByRoleSchema = z.record(AccountStatusIdSchema, AccountStatsByStatusSchema).and(
  z.object({
    lead: AccountStatsByStatusSchema.optional(),
    sat: AccountStatsByStatusSchema.optional(),
    set: AccountStatsByStatusSchema.optional(),
    closed: AccountStatsByStatusSchema.optional(),
    approved: AccountStatsByStatusSchema.optional(),
    installed: AccountStatsByStatusSchema.optional(),
    archived: AccountStatsByStatusSchema.optional(),
    dnc: AccountStatsByStatusSchema.optional(),
  })
)
export type AccountStatsByRole = z.infer<typeof AccountStatsByRoleSchema>

const AccountStatsSchema = z.object({
  setter: AccountStatsByRoleSchema.optional(),
  closer: AccountStatsByRoleSchema.optional(),
  selfgen: AccountStatsByRoleSchema.optional(),
})
export type AccountStats = z.infer<typeof AccountStatsSchema>

// TODO: try out using z.templateLiteral
export const ReportKpiTypeSchema = z.string()
export type ReportKpiType =
  | ReportAccountField
  | ReportAccountStatsField
  | ReportAccountWorkflowStatsField
  | ReportCardField
  | ReportWorkflowField
  | ReportActionField
  | ReportContactField
  | ReportConversationField
  | ReportConversationWorkflowField
  | ReportCustomFieldField
  | ReportEvaluationField
  | ReportEventField
  | ReportGameField
  | ReportInterviewField
  | ReportMeetingAttendanceField
  | ReportMessageField
  | ReportProgramKpiField
  | ReportProgramField
  | ReportQuestionField
  | ReportRatioField
  | ReportRecordingField
  | ReportTaskField
  | ReportTaskKpiField
  | ReportTeamField
  | ReportTeamProgramKpiField
  | ReportTeamTaskKpiField
  | ReportUserField
  | (string & {})

export type ReportActionField = 'action.hoursWorked' | 'action.daysWorked' | 'action.headcount' | 'action.accounts'

export type ReportCardField = 'card.sent' | 'card.views'

export type ReportContactField =
  | 'contact.count'
  | 'contact.users'
  | 'contact.pitches'
  | 'contact.doorPitches'
  | 'contact.phonePitches'
  | 'contact.firstPitchOfDay'
  | 'contact.lastPitchOfDay'
  | 'contact.firstKnockOfDay'
  | 'contact.lastKnockOfDay'
  | 'contact.hoursWorked'
  | 'contact.daysWorked'
  | 'contact.teamDaysWorked'
  | 'contact.lastPitchDate'
  | 'contact.lastKnockDate'
  | 'contact.avgPitchPerDay'
  | 'contact.avgPitchPerWeek'
  | 'contact.teamAvgPitchPerDay'
  | 'contact.teamAvgPitchPerWeek'
  | `contact.daysSince|${number}`
  | 'contact.interactions'
  | ReportContactDispositionField
export type ReportContactDispositionField = `disposition.${DispositionId}`
export const isReportContactDispositionField = (field: string): field is ReportContactDispositionField => {
  return field.startsWith('disposition.')
}

export type ReportEventField =
  | 'event.count'
  | 'event.users'
  | 'event.sits'
  | 'event.closes'
  | 'event.reschedules'
  | 'event.shadows'
  | 'event.lastInterviewDate'
  | 'event.nextInterviewDate'
  | 'event.status'
  | 'event.daysWorked'
  | ReportEventTypeField
export type ReportEventTypeField = `eventType.${EventType}`

export const ReportAccountWorkflowMetricSchema = z.union([
  AccountStatsByStatusKeySchema,
  WorkflowStageIdSchema,
  z.templateLiteral(['daysSince|', z.number()]),
])
export type ReportAccountWorkflowMetric = z.infer<typeof ReportAccountWorkflowMetricSchema>

export const ReportWorkflowActionMetricSchema = z.enum(['count', 'lastDate', 'avgStartOfDay', 'avgEndOfDay'])
export type ReportWorkflowActionMetric = z.infer<typeof ReportWorkflowActionMetricSchema>

export const ReportAccountWorkflowStatsFieldSchema = z.templateLiteral([
  AccountRoleSchema,
  '|',
  WorkflowStageIdSchema,
  '|',
  ReportAccountWorkflowMetricSchema,
])
export const isReportAccountWorkflowStatsField = (val: string): val is ReportAccountWorkflowStatsField =>
  ReportAccountWorkflowStatsFieldSchema.safeParse(val).success
export type ReportAccountWorkflowStatsField = string

export const ReportAccountStatsFieldSchema = z.templateLiteral([
  AccountRoleSchema,
  '.',
  AccountStatsByRoleKeysSchema,
  '.',
  z.union([AccountStatsByStatusKeySchema, AccountStatusIdSchema, z.templateLiteral(['daysSince|', z.number()])]),
])
export const isReportAccountStatsField = (val: string): val is ReportAccountStatsField =>
  ReportAccountStatsFieldSchema.safeParse(val).success
export type ReportAccountStatsField = string

export const WORKFLOW_REPORTING_MODES = ['setterCloser', 'actorContributor', 'legacy'] as const
export type WorkflowReportingMode = (typeof WORKFLOW_REPORTING_MODES)[number]

export const WorkflowFieldEntitySchema = z.enum(['account', 'actor', 'setter', 'closer'])
export type WorkflowFieldEntity = z.infer<typeof WorkflowFieldEntitySchema>

export const ReportWorkflowFieldSchema = z.templateLiteral([
  WorkflowFieldEntitySchema,
  '|',
  WorkflowStageIdSchema,
  '|',
  ReportAccountWorkflowMetricSchema,
])
export type ReportWorkflowField = z.infer<typeof ReportWorkflowFieldSchema>

export type ReportRatioField = `ratio|${string}|${string}`
export const isReportRatioField = (val: string): val is ReportRatioField => val.startsWith('ratio|')
export type ReportAccountField =
  | 'account.lead'
  | 'account.set'
  | 'account.sit'
  | 'account.sat'
  | 'account.closed'
  | 'account.approved'
  | 'account.installed'
  | 'account.lastLeadDate'
  | 'account.lastSetDate'
  | 'account.lastSatDate'
  | 'account.lastCloseDate'
  | 'account.lastApprovalDate'
  | 'account.lastInstallDate'
export type ReportTaskField = 'task.overdue'
export type ReportProgramField =
  | `program.tasks.${ProgramId | TaskOriginId}`
  | `program.team.tasks.${ProgramId | TaskOriginId}`
export const isReportProgramField = (val: string): val is ReportProgramField => val.startsWith('program.')

export const ReportTaskKpiMetricSchema = z.enum([
  'completionRate',
  'completed',
  'lateSubmission',
  'overdue',
  'target',
  'progress',
  'count',
  'completedAt',
])
export type ReportTaskKpiMetric = z.infer<typeof ReportTaskKpiMetricSchema>

export const TaskKpiIdSchema = z.union([TaskOriginIdSchema, ProgramTaskIdSchema])
export type TaskKpiId = z.infer<typeof TaskKpiIdSchema>

export const ReportTaskKpiFieldSchema = z.templateLiteral(['task|', TaskKpiIdSchema, '|', ReportTaskKpiMetricSchema])
export type ReportTaskKpiField = z.infer<typeof ReportTaskKpiFieldSchema>
export const isReportTaskKpiField = (val: string): val is ReportTaskKpiField => val.startsWith('task|')

export const ReportTeamTaskKpiMetricSchema = z.enum(['completionRate', 'completedAt'])
export type ReportTeamTaskKpiMetric = z.infer<typeof ReportTeamTaskKpiMetricSchema>

export const ReportTeamTaskKpiFieldSchema = z.templateLiteral([
  'task|team|',
  TaskKpiIdSchema,
  '|',
  ReportTeamTaskKpiMetricSchema,
])
export type ReportTeamTaskKpiField = z.infer<typeof ReportTeamTaskKpiFieldSchema>
export const isReportTeamTaskKpiField = (val: string): val is ReportTeamTaskKpiField => val.startsWith('task|team|')

export const ReportProgramKpiMetricSchema = z.enum([
  'completionRate',
  'completed',
  'lateSubmission',
  'overdue',
  'target',
  'progress',
  'count',
  'completedAt',
  'startDate',
])
export type ReportProgramKpiMetric = z.infer<typeof ReportProgramKpiMetricSchema>

export const ReportProgramKpiFieldSchema = z.templateLiteral([
  'program|',
  ProgramIdSchema,
  '|',
  ReportProgramKpiMetricSchema,
])
export type ReportProgramKpiField = z.infer<typeof ReportProgramKpiFieldSchema>
export const isReportProgramKpiField = (val: string): val is ReportProgramKpiField => val.startsWith('program|')

export const ReportTeamProgramKpiFieldSchema = z.templateLiteral([
  'program|team|',
  ProgramIdSchema,
  '|',
  ReportTeamTaskKpiMetricSchema,
])
export type ReportTeamProgramKpiField = z.infer<typeof ReportTeamProgramKpiFieldSchema>
export const isReportTeamProgramKpiField = (val: string): val is ReportTeamProgramKpiField =>
  val.startsWith('program|team|')

export type ReportTeamField = 'team.managers' | `team.${number}.name`
export type ReportMeetingAttendanceField = 'meetingAttendance.count' | 'meetingAttendance.lastDate'
export type ReportMessageField = 'message.count'
export type ReportEvaluationField =
  | 'evaluation.submittedCount'
  | 'evaluation.count'
  | 'evaluation.averageScore'
  | 'evaluation.evaluatedCount'
  | 'evaluation.evaluatedAverageScore'
export type ReportInterviewField = 'interview.completedThisQuarter' | 'interview.pending' | 'interview.completed'
export type ReportUserField = 'user.firstAccessDate' | 'user.lastAccessDate' | 'user.count' | 'user.teams'
export type ReportConversationField = 'conversation.averageScore' | 'conversation.count' | 'conversation.activeHours'

export const ReportConversationMetricSchema = z.enum(['count', 'averageScore', 'activeHours'])
export type ReportConversationMetric = z.infer<typeof ReportConversationMetricSchema>

export const ReportConversationWorkflowFieldSchema = z.templateLiteral([
  'conversation',
  '|',
  WorkflowStageIdSchema,
  '|',
  ReportConversationMetricSchema,
])
export type ReportConversationWorkflowField = z.infer<typeof ReportConversationWorkflowFieldSchema>
export const isReportConversationWorkflowField = (val: string): val is ReportConversationWorkflowField =>
  ReportConversationWorkflowFieldSchema.safeParse(val).success
export type ReportRecordingField =
  | 'recording.activeUsers'
  | 'recording.activeHours'
  | 'recording.activeMinutes'
  | 'recording.activeSeconds'
export const ReportQuestionMetricSchema = z.enum(['choiceIndex', 'skip', 'answer'])
export type ReportQuestionMetric = z.infer<typeof ReportQuestionMetricSchema>

export const ReportQuestionChoiceFieldSchema = z.templateLiteral([
  'question',
  '|',
  QuestionIdSchema,
  '|',
  z.enum(['choice', 'choicePercent']),
  '|',
  z.string().regex(/^\d+$/),
])
export type ReportQuestionChoiceField = z.infer<typeof ReportQuestionChoiceFieldSchema>

export const ReportQuestionFieldSchema = z.union([
  z.templateLiteral(['question', '|', QuestionIdSchema, '|', ReportQuestionMetricSchema]),
  ReportQuestionChoiceFieldSchema,
])
export type ReportQuestionField = z.infer<typeof ReportQuestionFieldSchema>

export const ReportGameFieldSchema = z.templateLiteral(['game', '|', GameIdSchema, '|', 'participantCount'])
export type ReportGameField = z.infer<typeof ReportGameFieldSchema>

export const ReportCustomFieldAggregationTypeSchema = z.enum(['count', 'sum'])
export type ReportCustomFieldAggregationType = z.infer<typeof ReportCustomFieldAggregationTypeSchema>

export const ReportCustomFieldAccountDateSchema = z.union([
  AccountStatusIdSchema,
  WorkflowStageIdSchema,
  z.literal('createdDate'),
])
export type ReportCustomFieldAccountDate = z.infer<typeof ReportCustomFieldAccountDateSchema>

const ReportAccountRoleCustomFieldFieldSchema = z.templateLiteral([
  'cf',
  '|',
  AccountRoleSchema,
  '|',
  ReportCustomFieldAggregationTypeSchema,
  '|',
  CustomFieldIdSchema,
  '|',
  ReportCustomFieldAccountDateSchema,
])
const ReportWorkflowCustomFieldFieldSchema = z.templateLiteral([
  'cf',
  '|account|',
  ReportCustomFieldAggregationTypeSchema,
  '|',
  CustomFieldIdSchema,
  '|',
  ReportCustomFieldAccountDateSchema,
])
const ReportCustomFieldFieldSchema = z.union([
  ReportAccountRoleCustomFieldFieldSchema,
  ReportWorkflowCustomFieldFieldSchema,
])
export const isReportCustomFieldField = (field: string): field is ReportCustomFieldField => {
  return ReportCustomFieldFieldSchema.safeParse(field).success
}
export type ReportCustomFieldField = z.infer<typeof ReportCustomFieldFieldSchema>

const ReportKpiDataSchema = z.object({
  ...AccountStatsSchema.shape,
  contact: z
    .object({
      count: z.number().optional(),
      users: z.number().optional(),
      pitches: z.number().optional(),
      leads: z.number().optional(),
      firstPitchOfDay: z.number().optional(),
      lastPitchOfDay: z.number().optional(),
      firstKnockOfDay: z.number().optional(),
      lastKnockOfDay: z.number().optional(),
      hoursWorked: z.number().optional(),
      daysWorked: z.number().optional(),
      lastPitchDate: z.number().optional(),
      lastKnockDate: z.number().optional(),
    })
    .optional(),
  event: z
    .record(z.string(), z.number().optional())
    .and(
      z.object({
        count: z.number().optional(),
        users: z.number().optional(),
        sits: z.number().optional(),
        closes: z.number().optional(),
        shadows: z.number().optional(),
        reschedules: z.number().optional(),
        lastInterviewDate: z.number().optional(),
        nextInterviewDate: z.number().optional(),
      })
    )
    .optional(),
  account: z
    .object({
      lead: z.number().optional(),
      setterSet: z.number().optional(),
      setterSat: z.number().optional(),
      closerSat: z.number().optional(),
      closed: z.number().optional(),
      approved: z.number().optional(),
      installed: z.number().optional(),
      setterSetsToSits: z.number().optional(),
      setterSetsToCloses: z.number().optional(),
      closerSitsToCloses: z.number().optional(),
      averageCloseAttempts: z.number().optional(),
      lastLeadDate: z.number().optional(),
      lastSetDate: z.number().optional(),
      lastSatDate: z.number().optional(),
      lastCloseDate: z.number().optional(),
      lastApprovalDate: z.number().optional(),
      lastInstallDate: z.number().optional(),
    })
    .optional()
    .meta({ deprecated: true }),
  task: z
    .object({
      overdue: z.number().optional(),
    })
    .optional(),
  meetingAttendance: z
    .object({
      count: z.number().optional(),
      lastDate: z.number().optional(),
    })
    .optional(),
  evaluation: z
    .object({
      count: z.number().optional(),
      averageScore: z.number().optional(),
      evaluatedCount: z.number().optional(),
      evaluatedAverageScore: z.number().optional(),
    })
    .optional(),
  plan: z
    .object({
      pitch: z.number().optional(),
      lead: z.number().optional(),
      install: z.number().optional(),
      totalClosesToInstalls: z.number().optional(),
      totalLeadsToCloses: z.number().optional(),
      totalPitchesToLeads: z.number().optional(),
      pending: z.number().optional(),
      submitted: z.number().optional(),
      approved: z.number().optional(),
      rejected: z.number().optional(),
      closed: z.number().optional(),
      quarterlyTargetPitches: z.number().optional(),
      quarterlyTargetLeads: z.number().optional(),
      quarterlyTargetCloses: z.number().optional(),
      quarterlyTargetInstalls: z.number().optional(),
      weeklyTargetPitches: z.number().optional(),
    })
    .optional(),
  interview: z
    .object({
      completedThisQuarter: z.number().optional(),
      pending: z.number().optional(),
      completed: z.number().optional(),
    })
    .optional(),
  message: z
    .object({
      count: z.number().optional(),
    })
    .optional(),
  user: z
    .object({
      firstAccessDate: z.number().optional(),
      lastAccessDate: z.number().optional(),
      count: z.number().optional(),
      teamIds: TeamIdSchema.array().optional(),
    })
    .optional(),
  team: z
    .object({
      managers: z.string().optional(),
    })
    .optional(),
  conversation: z
    .object({
      averageScore: z.number().optional(),
      count: z.number().optional(),
    })
    .optional(),
})

export type ReportKpiData = z.infer<typeof ReportKpiDataSchema>

const ReportKpiUserRowSchema = z.object({
  type: z.literal('user'),
  user: TinyUserSchema,
  data: ReportKpiDataSchema,
  isDeleted: z.boolean().optional(),
})
export type ReportKpiUserRow = z.infer<typeof ReportKpiUserRowSchema>

const ReportKpiTeamRowSchema = z.object({
  type: z.literal('team'),
  team: TinyTeamSchema,
  data: ReportKpiDataSchema,
})
export type ReportKpiTeamRow = z.infer<typeof ReportKpiTeamRowSchema>

const ReportKpiTimePeriodRowSchema = z.object({
  type: z.literal('time'),
  range: z.object({
    startDate: z.number(),
  }),
  data: ReportKpiDataSchema,
})
export type ReportKpiTimePeriodRow = z.infer<typeof ReportKpiTimePeriodRowSchema>

export const ReportKpiRowSchema = z.discriminatedUnion('type', [
  ReportKpiUserRowSchema,
  ReportKpiTeamRowSchema,
  ReportKpiTimePeriodRowSchema,
])
export type ReportKpiRow = z.infer<typeof ReportKpiRowSchema>

const STANDARD_GAME_BOOSTED_KPIS = [
  'contact.pitches' satisfies ReportKpiType, // TODO: zod schema
  'contact.daysWorked' satisfies ReportKpiType,
  'meetingAttendance.count' satisfies ReportKpiType,
] as const
export const GameBoostedReportKpiSchema = z.union([
  z.enum(STANDARD_GAME_BOOSTED_KPIS),
  ReportAccountStatsFieldSchema,
  ReportAccountRoleCustomFieldFieldSchema,
])
export type GameBoostedReportKpi = z.infer<typeof GameBoostedReportKpiSchema>
export const isGameBoostedReportKpi = (val: string): val is GameBoostedReportKpi =>
  GameBoostedReportKpiSchema.safeParse(val).success
